import React from 'react'

// Minimal symbol configuration
export interface MinimalSymbol {
  id: string
  name: string
  category: string
  tags: string[]
  // JSX SVG content instead of string
  svgContent: React.ReactElement
  viewBox: string
  // Simple size categories
  size: 'small' | 'medium' | 'large' | 'wide'
  // Connection points
  handles: ('top' | 'bottom' | 'left' | 'right')[]
}

// Size mappings
export const SYMBOL_SIZES = {
  small: { width: '2.5rem', height: '2.5rem' },
  medium: { width: '3rem', height: '3rem' },
  large: { width: '3.5rem', height: '3.5rem' },
  wide: { width: '3rem', height: '4rem' },
}

// All existing symbols converted to JSX format
export const MINIMAL_SYMBOLS: MinimalSymbol[] = [
  // Basic shapes
  {
    id: 'circle',
    name: 'Circle',
    category: 'basic',
    tags: ['circle', 'round', 'basic', 'shape'],
    svgContent: (
      <circle
        cx='50'
        cy='50'
        r='40'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: ['top', 'left', 'bottom', 'right'],
  },
  {
    id: 'square',
    name: 'Square',
    category: 'basic',
    tags: ['square', 'rectangle', 'basic', 'shape'],
    svgContent: (
      <rect
        x='10'
        y='10'
        width='80'
        height='80'
        rx='5'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: ['left', 'right'],
  },
  {
    id: 'diamond',
    name: 'Diamond',
    category: 'basic',
    tags: ['diamond', 'rhombus', 'decision', 'shape'],
    svgContent: (
      <path
        d='M50,10 L90,50 L50,90 L10,50 Z'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: ['top', 'right', 'bottom', 'left'],
  },
  {
    id: 'inputNode',
    name: 'Input',
    category: 'basic',
    tags: ['input', 'start', 'entry'],
    svgContent: (
      <path
        d='M10,50 L90,10 L90,90 Z'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: ['right'],
  },
  {
    id: 'outputNode',
    name: 'Output',
    category: 'basic',
    tags: ['output', 'end', 'exit'],
    svgContent: (
      <path
        d='M10,10 L10,90 L90,50 Z'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: ['left'],
  },
  {
    id: 'labelNode',
    name: 'Label',
    category: 'basic',
    tags: ['label', 'text', 'annotation'],
    svgContent: (
      <rect
        x='10'
        y='30'
        width='80'
        height='40'
        fill='none'
        stroke='currentColor'
        strokeWidth='3'
        strokeDasharray='5,5'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'large',
    handles: [],
  },

  // Instruments
  {
    id: 'support_skirt',
    name: 'Support Skirt',
    category: 'instruments',
    tags: ['support', 'skirt', 'vessel', 'vertical', 'structure'],
    svgContent: (
      <path
        d='M0,0 L0,100 L20,100 M100,0 L100,100 L80,100'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['top', 'bottom'],
  },
  {
    id: 'support_ring',
    name: 'Support Ring',
    category: 'instruments',
    tags: ['support', 'ring', 'vessel', 'horizontal', 'structure'],
    svgContent: (
      <path
        d='M0,40 L0,60 L20,60 L20,40 M80,40 L80,60 L100,60 L100,40'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['left', 'right'],
  },
  {
    id: 'support_leg',
    name: 'Support Leg',
    category: 'instruments',
    tags: ['support', 'leg', 'vessel', 'elevated', 'structure'],
    svgContent: (
      <path
        d='M0,0 L0,100 M100,0 L100,100 M0,80 L20,80 M80,80 L100,80'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['top', 'bottom'],
  },
  {
    id: 'support_bracket',
    name: 'Support Bracket',
    category: 'instruments',
    tags: ['support', 'bracket', 'vessel', 'wall', 'mount'],
    svgContent: (
      <path
        d='M0,20 L20,20 L20,80 L0,80 M20,40 L100,40 M20,60 L100,60'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['right'],
  },
  {
    id: 'socket_connection_nozzle',
    name: 'Socket Connection Nozzle',
    category: 'instruments',
    tags: ['nozzle', 'connection', 'socket', 'weld', 'fitting'],
    svgContent: (
      <path
        d='M0,40 L30,40 L30,20 L70,20 L70,80 L30,80 L30,60 L0,60 M70,35 L100,35 L100,65 L70,65'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['left', 'right'],
  },
  {
    id: 'manhole',
    name: 'Manhole',
    category: 'instruments',
    tags: ['manhole', 'access', 'entry', 'vessel', 'opening'],
    svgContent: (
      <path
        d='M100,0 L100,100 M80,0 L80,20 L0,20 M80,100 L80,80 L0,80'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 100',
    size: 'medium',
    handles: ['left', 'right'],
  },

  // Dryers
  {
    id: 'drier',
    name: 'Basic Drier',
    category: 'dryers',
    tags: ['drier', 'dryer', 'drying', 'basic', 'general'],
    svgContent: (
      <path
        d='M10 0 L90 0 L100 25 L100 140 L0 140 L0 25 Z'
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
      />
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'spray_drier',
    name: 'Spray Drier',
    category: 'dryers',
    tags: ['spray', 'drier', 'dryer', 'atomization', 'liquid'],
    svgContent: (
      <>
        <path
          d='M10 0 L90 0 L100 25 L100 140 L0 140 L0 25 Z'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          d='M40 10 L50 0 L60 10 M50 0 L50 10'
          stroke='currentColor'
          fill='none'
          strokeWidth='3'
        />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'fluidized_bed_drier',
    name: 'Fluidized Bed Drier',
    category: 'dryers',
    tags: ['fluidized', 'bed', 'drier', 'dryer', 'fluid', 'suspension'],
    svgContent: (
      <>
        <path
          d='M10 0 L90 0 L100 25 L100 140 L0 140 L0 25 Z'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          d='M0 40 L100 40 M0 70 L100 70'
          stroke='currentColor'
          fill='none'
          strokeWidth='3'
        />
        {/* Particle representation */}
        {[15, 30, 45, 60, 75, 85].map((x, index) => (
          <circle
            key={index}
            cx={x}
            cy={55 + (index % 2 === 0 ? -5 : 5)}
            r='1'
            stroke='currentColor'
            fill='currentColor'
            strokeWidth='2'
          />
        ))}
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'rotary_drum_drier',
    name: 'Rotary Drum Drier',
    category: 'dryers',
    tags: ['rotary', 'drum', 'drier', 'dryer', 'tumbling', 'rotating'],
    svgContent: (
      <>
        <ellipse
          cx='50'
          cy='70'
          rx='45'
          ry='60'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          d='M20,50 L80,50 M30,90 L70,90'
          stroke='currentColor'
          strokeWidth='3'
        />
        <circle cx='50' cy='70' r='3' fill='currentColor' />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'shelf_dryer',
    name: 'Shelf Dryer',
    category: 'dryers',
    tags: ['shelf', 'dryer', 'oven', 'chamber', 'tray', 'batch'],
    svgContent: (
      <>
        <rect
          x='10'
          y='0'
          width='80'
          height='140'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          d='M10,30 L90,30 M10,60 L90,60 M10,90 L90,90 M10,120 L90,120'
          stroke='currentColor'
          strokeWidth='3'
        />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'heat_consumer',
    name: 'Heat Consumer',
    category: 'dryers',
    tags: ['heat', 'consumer', 'thermal', 'energy', 'heating'],
    svgContent: (
      <>
        <rect
          x='10'
          y='20'
          width='80'
          height='100'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path
          d='M30,40 L70,40 M30,60 L70,60 M30,80 L70,80 M30,100 L70,100'
          stroke='currentColor'
          strokeWidth='3'
        />
        <path
          d='M40,0 L40,20 M50,0 L50,20 M60,0 L60,20'
          stroke='currentColor'
          strokeWidth='4'
        />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'turbo_drier',
    name: 'Turbo Drier',
    category: 'dryers',
    tags: ['turbo', 'disc', 'drier', 'dryer', 'moving', 'shelf'],
    svgContent: (
      <>
        <ellipse
          cx='50'
          cy='70'
          rx='45'
          ry='60'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <circle cx='50' cy='70' r='5' fill='currentColor' />
        <path
          d='M50,30 L50,110 M20,50 L80,50 M30,90 L70,90'
          stroke='currentColor'
          strokeWidth='3'
        />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
  {
    id: 'conveyor_belt_drier',
    name: 'Conveyor Belt Drier',
    category: 'dryers',
    tags: ['conveyor', 'belt', 'drier', 'dryer', 'continuous', 'roller'],
    svgContent: (
      <>
        <rect
          x='0'
          y='40'
          width='100'
          height='60'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
        />
        <path d='M0,80 L100,80' stroke='currentColor' strokeWidth='4' />
        <circle
          cx='15'
          cy='90'
          r='8'
          fill='none'
          stroke='currentColor'
          strokeWidth='3'
        />
        <circle
          cx='85'
          cy='90'
          r='8'
          fill='none'
          stroke='currentColor'
          strokeWidth='3'
        />
        <path
          d='M20,0 L20,40 M40,0 L40,40 M60,0 L60,40 M80,0 L80,40'
          stroke='currentColor'
          strokeWidth='3'
        />
      </>
    ),
    viewBox: '0 0 100 140',
    size: 'wide',
    handles: ['top', 'bottom', 'left', 'right'],
  },
]

// Helper functions
export const getSymbolsByCategory = (categoryId: string) =>
  MINIMAL_SYMBOLS.filter((symbol) => symbol.category === categoryId)

export const getSymbolById = (id: string) =>
  MINIMAL_SYMBOLS.find((symbol) => symbol.id === id)

export const getAllCategories = () =>
  Array.from(new Set(MINIMAL_SYMBOLS.map((symbol) => symbol.category)))

export const searchSymbols = (query: string) => {
  const lowerQuery = query.toLowerCase()
  return MINIMAL_SYMBOLS.filter(
    (symbol) =>
      symbol.name.toLowerCase().includes(lowerQuery) ||
      symbol.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  )
}
